import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { Sparkles, Check, Rocket, Lock } from 'lucide-react';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Botão CTA (gradiente oficial da marca) — usado no fluxo de criar senha (caso 1).
const ctaStyle = {
  background: 'linear-gradient(135deg, #0DAEFF, #06B6D4)',
  border: 'none', borderRadius: '11px', padding: '14px 28px',
  fontSize: '16px', fontWeight: 700, color: '#FFFFFF',
  fontFamily: "'Outfit', sans-serif", letterSpacing: '0.3px',
  width: '100%', transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
};
const inputBase = {
  width: '100%', padding: '12px', marginBottom: '8px',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '9px', color: '#FFFFFF', fontSize: '14px',
  fontFamily: "'Outfit', sans-serif", outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

/**
 * WelcomeModal — boas-vindas no 1º acesso.
 *
 * 3 casos (regra do dono):
 *   1. has_password=false (entrou por Google) → criar senha OBRIGATÓRIA. O modal
 *      NÃO fecha sem isso (sem ESC, sem clique-fora, sem botão de pular; o único
 *      botão cria a senha e só então prossegue).
 *   2. has_password=true & has_google=false → oferece vincular Google (OPCIONAL);
 *      pode ativar normal.
 *   3. has_password=true & has_google=true → boas-vindas normal; pode ativar.
 */
const WelcomeModal = ({
  isOpen,
  onActivate,
  hasPassword = true,
  hasGoogle = true,
  onPasswordCreated,
  onGoogleLinked,
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleLinked, setGoogleLinked] = useState(false);
  const [googleError, setGoogleError] = useState('');

  // Bloqueia scroll do body + ESC (modal não fecha por ESC).
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => { if (e.key === 'Escape') e.preventDefault(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // CASO 1 — cria a senha e SÓ ENTÃO prossegue (onActivate).
  const handleCreatePassword = async () => {
    setError('');
    if (password.length < 8) {
      setError(t('welcome.password_too_short') || 'A senha precisa ter no mínimo 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError(t('welcome.password_mismatch') || 'As senhas não coincidem.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/set-password`, { new_password: password }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onPasswordCreated) onPasswordCreated();  // App: has_password = true
      if (onActivate) onActivate();                // marca welcome_seen + fecha
    } catch (err) {
      setError(err.response?.data?.detail || (t('welcome.password_error') || 'Erro ao criar a senha. Tente novamente.'));
      setSubmitting(false);
    }
  };

  // CASO 2 — vínculo Google opcional (amarrado ao email da conta no backend).
  const handleLinkGoogle = async (credentialResponse) => {
    setGoogleError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/link-google`, { credential: credentialResponse.credential }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoogleLinked(true);
      if (onGoogleLinked) onGoogleLinked();  // App: has_google = true
    } catch (err) {
      setGoogleError(err.response?.data?.detail || (t('welcome.google_link_error') || 'Não foi possível vincular o Google.'));
    }
  };

  const focusBlue = (e) => { e.target.style.borderColor = '#0DAEFF'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.20)'; };
  const blurBase = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999,
        animation: 'wm-fadeIn 0.3s ease-out',
        cursor: 'default',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="wm-modal"
        style={{
          background: 'linear-gradient(180deg, #1a1d24 0%, #141414 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '18px',
          padding: '32px 28px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '92vh',
          overflowY: 'auto',
          textAlign: 'center',
          boxShadow: '0 0 60px rgba(0,0,0,0.5), 0 0 120px rgba(0,0,0,0.3)',
        }}
      >
        {/* Logo */}
        <div className="wm-logo" style={{ marginBottom: '14px', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/logo-darkplanner.png"
            alt="DarkPlanner"
            style={{ width: '54px', height: '54px', borderRadius: '13px', objectFit: 'cover' }}
          />
        </div>

        {/* Headline */}
        <h1 className="wm-headline" style={{ fontSize: '22px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Sparkles size={20} style={{ color: '#0DAEFF', flexShrink: 0 }} />
          {t('welcome.headline')}
        </h1>

        {/* Subheadline */}
        <p className="wm-subheadline" style={{ fontSize: '13px', color: '#0DAEFF', margin: '0 0 16px 0' }}>
          {t('welcome.subheadline')}
        </p>

        {/* Manifesto — casos 2 e 3. No caso 1 (sem senha) o foco é o campo de senha. */}
        {hasPassword && (
          <>
            {/* Divider 1 */}
            <div className="wm-divider-1" style={{ height: '1px', margin: '16px 0', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }} />

            {/* Body */}
            <div className="wm-body" style={{ textAlign: 'left', marginBottom: '4px' }}>
              <p style={{ fontSize: '13px', color: '#9CA3AF', lineHeight: 1.55, margin: '0 0 8px 0' }}>{t('welcome.body_1')}</p>
              <p style={{ fontSize: '13px', color: '#9CA3AF', lineHeight: 1.55, margin: '0 0 8px 0' }}>{t('welcome.body_2')}</p>
            </div>

            {/* Divider 2 */}
            <div className="wm-divider-2" style={{ height: '1px', margin: '16px 0', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }} />

            {/* Bullets */}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
              {[1, 2, 3, 4].map((n) => (
                <li key={n} className={`wm-bullet-${n}`} style={{ fontSize: '13px', color: '#FFFFFF', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    flexShrink: 0, width: '20px', height: '20px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={12} strokeWidth={3} style={{ color: '#FFFFFF' }} />
                  </span>
                  {t(`welcome.bullet_${n}`)}
                </li>
              ))}
            </ul>

            {/* Divider 3 */}
            <div className="wm-divider-3" style={{ height: '1px', margin: '16px 0', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }} />
          </>
        )}

        {/* ─── AÇÃO FINAL (condicional) ─── */}
        {!hasPassword ? (
          // CASO 1 — Google sem senha: criar senha OBRIGATÓRIA (não fecha sem isso).
          <div className="wm-action-pw" style={{
            textAlign: 'left',
            border: '1px solid rgba(13,174,255,0.35)',
            background: 'rgba(13,174,255,0.06)',
            borderRadius: '14px',
            padding: '18px',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF', textAlign: 'center', margin: '0 0 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Lock size={16} style={{ color: '#0DAEFF', flexShrink: 0 }} />
              {t('welcome.create_password_title') || 'Crie sua senha de acesso'}
            </h3>
            <p style={{ fontSize: '12.5px', color: '#9CA3AF', textAlign: 'center', lineHeight: 1.5, margin: '0 0 14px' }}>
              {t('welcome.create_password_desc') || 'Você entrou pelo Google. Defina uma senha para também entrar por email e senha — ela é necessária para o aplicativo desktop.'}
            </p>

            <div style={{ position: 'relative' }}>
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('welcome.password_placeholder') || 'Nova senha (mínimo 8 caracteres)'}
                autoComplete="new-password"
                style={{ ...inputBase, paddingRight: '64px' }}
                onFocus={focusBlue}
                onBlur={blurBase}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                style={{ position: 'absolute', right: '12px', top: '11px', background: 'none', border: 'none', color: '#9CA3AF', fontSize: '12px', cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}
              >
                {showPwd ? (t('welcome.hide') || 'ocultar') : (t('welcome.show') || 'mostrar')}
              </button>
            </div>

            <input
              type={showPwd ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={t('welcome.password_confirm_placeholder') || 'Confirmar senha'}
              autoComplete="new-password"
              style={inputBase}
              onFocus={focusBlue}
              onBlur={blurBase}
            />

            {error && (
              <p style={{ color: '#F87171', fontSize: '13px', margin: '2px 0 10px', textAlign: 'center' }}>{error}</p>
            )}

            <button
              onClick={handleCreatePassword}
              disabled={submitting || !password || !confirm}
              style={{
                ...ctaStyle,
                marginTop: '4px',
                opacity: (submitting || !password || !confirm) ? 0.55 : 1,
                cursor: (submitting || !password || !confirm) ? 'default' : 'pointer',
              }}
              onMouseEnter={(e) => { if (!submitting && password && confirm) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(13,174,255,0.45)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {submitting ? (t('welcome.creating') || 'Criando...') : (t('welcome.create_password_button') || 'Criar senha e ativar o sistema')}
            </button>

            <p style={{ fontSize: '11px', color: '#6B7280', textAlign: 'center', margin: '10px 0 0', lineHeight: 1.45 }}>
              {t('welcome.password_required_hint') || 'A senha é obrigatória para continuar e serve para fazer login depois (web e app desktop).'}
            </p>
          </div>
        ) : (
          // CASOS 2 e 3 — pode ativar.
          <div className="wm-action">
            {/* CASO 2 — vincular Google (opcional). */}
            {!hasGoogle && GOOGLE_CLIENT_ID && (
              <div style={{
                marginBottom: '16px', padding: '14px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <p style={{ fontSize: '13px', color: '#E5E7EB', margin: '0 0 10px', fontWeight: 600 }}>
                  {t('welcome.link_google_title') || 'Entrar também com o Google (opcional)'}
                </p>
                {googleLinked ? (
                  <p style={{ fontSize: '13px', color: '#34D399', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={14} strokeWidth={3} style={{ flexShrink: 0 }} /> {t('welcome.google_linked') || 'Google vinculado'}
                  </p>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                      onSuccess={handleLinkGoogle}
                      onError={() => setGoogleError(t('welcome.google_link_error') || 'Não foi possível vincular o Google.')}
                      theme="filled_black"
                      text="continue_with"
                      shape="pill"
                    />
                  </div>
                )}
                {googleError && (
                  <p style={{ color: '#F87171', fontSize: '12px', margin: '8px 0 0', textAlign: 'center' }}>{googleError}</p>
                )}
              </div>
            )}

            {/* Botão de ativar (mantido como hoje). */}
            <button
              className="wm-button"
              onClick={onActivate}
              style={{
                background: 'linear-gradient(135deg, #0DAEFF, #06B6D4)',
                border: 'none', borderRadius: '12px',
                padding: '14px 32px',
                fontSize: '16px', fontWeight: 700,
                color: '#FFFFFF', cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                letterSpacing: '0.5px',
                fontFamily: "'Outfit', sans-serif",
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(13,174,255,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <Rocket size={18} style={{ flexShrink: 0 }} />
              {t('welcome.button')}
            </button>
          </div>
        )}
      </div>

      {/* ═══════ ANIMATIONS ═══════ */}
      <style>{`
        @keyframes wm-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wm-logoEntrance {
          0% { transform: scale(1.5) translateY(100px); opacity: 0; }
          30% { transform: scale(1.5) translateY(100px) rotate(360deg); opacity: 1; }
          100% { transform: scale(1) translateY(0) rotate(360deg); opacity: 1; }
        }
        @keyframes wm-slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes wm-fadeSoft { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wm-bulletSlide { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes wm-scaleIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes wm-glowPulse { 0%, 100% { box-shadow: 0 0 20px rgba(13,174,255,0.3); } 50% { box-shadow: 0 0 40px rgba(13,174,255,0.6); } }

        .wm-logo { animation: wm-logoEntrance 0.9s ease-out forwards; }
        .wm-headline { opacity: 0; animation: wm-slideUp 0.3s ease-out 0.9s forwards; }
        .wm-subheadline { opacity: 0; animation: wm-fadeSoft 0.3s ease-out 1.2s forwards; }
        .wm-divider-1 { opacity: 0; animation: wm-fadeSoft 0.3s ease-out 1.5s forwards; }
        .wm-body { opacity: 0; animation: wm-fadeSoft 0.4s ease-out 1.8s forwards; }
        .wm-divider-2 { opacity: 0; animation: wm-fadeSoft 0.3s ease-out 2.2s forwards; }
        .wm-bullet-1 { opacity: 0; animation: wm-bulletSlide 0.3s ease-out 2.5s forwards; }
        .wm-bullet-2 { opacity: 0; animation: wm-bulletSlide 0.3s ease-out 2.65s forwards; }
        .wm-bullet-3 { opacity: 0; animation: wm-bulletSlide 0.3s ease-out 2.8s forwards; }
        .wm-bullet-4 { opacity: 0; animation: wm-bulletSlide 0.3s ease-out 2.95s forwards; }
        .wm-divider-3 { opacity: 0; animation: wm-fadeSoft 0.3s ease-out 3.1s forwards; }
        /* Caso 1 (senha): card revela rápido (1.3s). Casos 2/3: ação fecha a sequência do manifesto (3.4s). */
        .wm-action-pw { opacity: 0; animation: wm-fadeSoft 0.4s ease-out 1.3s forwards; }
        .wm-action { opacity: 0; animation: wm-fadeSoft 0.4s ease-out 3.4s forwards; }
        .wm-button { animation: wm-glowPulse 2s ease-in-out 4s infinite; }

        .wm-modal::-webkit-scrollbar { width: 6px; }
        .wm-modal::-webkit-scrollbar-track { background: transparent; }
        .wm-modal::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default WelcomeModal;
